import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { GoSearch } from 'react-icons/go';
import { RootState } from '../../store';

interface FilterSidebarProps {
    hideHeader?: boolean;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ hideHeader }) => {
    const { categories } = useSelector((state: RootState) => state.category);

    const [isMobile, setIsMobile] = useState(false);
    const [openSection, setOpenSection] = useState<string | null>(null);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 900;
            setIsMobile(mobile);
            if (!mobile) {
                setOpenSection(null);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleSection = (section: string) => {
        if (isMobile) {
            setOpenSection(openSection === section ? null : section);
        }
    };

    // Mock brands for now
    const brands = [
        { name: 'Roadster', count: 245 },
        { name: 'Nike', count: 180 },
        { name: 'Adidas', count: 150 },
        { name: 'Levi\'s', count: 120 }
    ];

    return (
        <div className="filter-sidebar-container">
            <style>
                {`
                .filter-sidebar-container {
                    width: 100%;
                    padding: 0 15px;
                }

                .filter-group-section {
                    padding: 20px 0;
                    border-bottom: 1px solid #f5f5f6;
                    position: relative;
                }

                .filter-group-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 18px;
                    cursor: default;
                }

                .filter-group-header-text {
                    font-size: 14px;
                    font-weight: 700;
                    text-transform: uppercase;
                    color: #282c3f;
                }

                .filter-group-options {
                    max-height: 200px;
                    overflow-y: auto;
                }

                /* Responsive mobile row layout */
                @media (max-width: 900px) {
                    .filter-sidebar-container {
                        display: flex !important;
                        flex-direction: row !important;
                        flex-wrap: nowrap !important;
                        overflow-x: auto !important;
                        padding: 12px 16px !important;
                        gap: 12px !important;
                        background-color: #ffffff !important;
                        scrollbar-width: none !important;
                        width: 100% !important;
                        box-sizing: border-box !important;
                    }
                    .filter-sidebar-container::-webkit-scrollbar {
                        display: none !important;
                    }
                    
                    .filter-group-section {
                        border-bottom: none !important;
                        padding: 0 !important;
                        flex-shrink: 0 !important;
                    }
                    
                    .filter-group-header {
                        border: 1px solid #e0e0e0 !important;
                        border-radius: 24px !important;
                        padding: 8px 16px !important;
                        margin-bottom: 0 !important;
                        cursor: pointer !important;
                        background-color: #ffffff !important;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.02) !important;
                        user-select: none !important;
                    }
                    .filter-group-header:active {
                        background-color: #f7f9fa !important;
                    }
                    
                    .filter-group-header-text {
                        font-size: 12.5px !important;
                        font-weight: 600 !important;
                        color: #444 !important;
                        text-transform: capitalize !important;
                    }
                    
                    .filter-group-options {
                        position: absolute !important;
                        top: calc(100% + 8px) !important;
                        left: 0 !important;
                        background: #ffffff !important;
                        border: 1px solid #eee !important;
                        border-radius: 12px !important;
                        box-shadow: 0 10px 30px rgba(0,0,0,0.12) !important;
                        padding: 16px !important;
                        z-index: 150 !important;
                        min-width: 240px !important;
                        max-height: 250px !important;
                    }

                    .mobile-chevron {
                        display: inline-block !important;
                    }

                    .filter-group-header-icon {
                        display: none !important;
                    }
                }

                .skeleton-list-item {
                    height: 16px;
                    background: #f1f5f9;
                    border-radius: 4px;
                    margin-bottom: 12px;
                    position: relative;
                    overflow: hidden;
                }

                .skeleton-list-shimmer {
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                    background: linear-gradient(
                        90deg,
                        rgba(255, 255, 255, 0) 0%,
                        rgba(255, 255, 255, 0.6) 50%,
                        rgba(255, 255, 255, 0) 100%
                    );
                    animation: list-shimmer 1.6s infinite;
                }

                @keyframes list-shimmer {
                    0% {
                        transform: translateX(-100%);
                    }
                    100% {
                        transform: translateX(100%);
                    }
                }
                `}
            </style>

            {!hideHeader && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                    <span style={{ fontSize: '15px', fontWeight: 700, color: '#282c3f', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Filters</span>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-primary)', cursor: 'pointer' }}>CLEAR ALL</span>
                </div>
            )}

            {/* Categories Section (Dynamic) */}
            <div className="filter-group-section">
                <div className="filter-group-header" onClick={() => toggleSection('categories')}>
                    <div className="filter-group-header-icon" style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#f5f5f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <GoSearch size="12px" color="#696e79" />
                    </div>
                    <div className="filter-group-header-text">Categories</div>
                    <span className="mobile-chevron" style={{ display: 'none', fontSize: '10px', color: '#696e79', marginLeft: '4px' }}>▼</span>
                </div>
                {(!isMobile || openSection === 'categories') && (
                    <div className="filter-group-options no-scrollbar">
                        {categories.length > 0 ? (
                            categories.slice(0, 10).map(cat => (
                                <label key={cat._id} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', marginBottom: '10px', color: '#282c3f' }}>
                                    <input type="checkbox" style={{ accentColor: 'var(--color-primary)', width: '16px', height: '16px' }} />
                                    <span style={{ textTransform: 'capitalize' }}>{cat.name}</span>
                                </label>
                            ))
                        ) : (
                            <div style={{ padding: '4px 0' }}>
                                {Array.from({ length: 5 }).map((_, idx) => (
                                    <div key={idx} className="skeleton-list-item" style={{ width: idx % 2 === 0 ? '75%' : '90%' }}>
                                        <div className="skeleton-list-shimmer" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Brand Section (Dynamicish) */}
            <div className="filter-group-section">
                <div className="filter-group-header" onClick={() => toggleSection('brands')}>
                    <div className="filter-group-header-icon" style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#f5f5f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <GoSearch size="12px" color="#696e79" />
                    </div>
                    <div className="filter-group-header-text">Brand</div>
                    <span className="mobile-chevron" style={{ display: 'none', fontSize: '10px', color: '#696e79', marginLeft: '4px' }}>▼</span>
                </div>
                {(!isMobile || openSection === 'brands') && (
                    <div className="filter-group-options no-scrollbar">
                        {brands.map(brand => (
                            <label key={brand.name} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', marginBottom: '10px', color: '#282c3f' }}>
                                <input type="checkbox" style={{ accentColor: 'var(--color-primary)', width: '16px', height: '16px' }} />
                                <span>{brand.name}</span>
                                <span style={{ fontSize: '11px', color: '#94969f', marginLeft: '4px' }}>({brand.count})</span>
                            </label>
                        ))}
                    </div>
                )}
            </div>

            {/* Price Section */}
            <div className="filter-group-section">
                <div className="filter-group-header" onClick={() => toggleSection('price')}>
                    <div className="filter-group-header-text">Price</div>
                    <span className="mobile-chevron" style={{ display: 'none', fontSize: '10px', color: '#696e79', marginLeft: '4px' }}>▼</span>
                </div>
                {(!isMobile || openSection === 'price') && (
                    <div className="filter-group-options no-scrollbar">
                        {[
                            'Rs. 500 to Rs. 1000',
                            'Rs. 1000 to Rs. 2500',
                            'Rs. 2500 to Rs. 5000'
                        ].map(range => (
                            <label key={range} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', marginBottom: '10px', color: '#282c3f' }}>
                                <input type="checkbox" style={{ accentColor: 'var(--color-primary)', width: '16px', height: '16px' }} />
                                <span>{range}</span>
                            </label>
                        ))}
                    </div>
                )}
            </div>

            {/* Color Section */}
            <div className="filter-group-section" style={{ borderBottom: 'none' }}>
                <div className="filter-group-header" onClick={() => toggleSection('color')}>
                    <div className="filter-group-header-text">Color</div>
                    <span className="mobile-chevron" style={{ display: 'none', fontSize: '10px', color: '#696e79', marginLeft: '4px' }}>▼</span>
                </div>
                {(!isMobile || openSection === 'color') && (
                    <div className="filter-group-options no-scrollbar">
                        {[
                            { name: 'Black', color: '#000000' },
                            { name: 'White', color: '#ffffff', border: '1px solid #eee' },
                            { name: 'Blue', color: '#dbeb83' },
                            { name: 'Red', color: '#e53935' }
                        ].map(c => (
                            <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px', cursor: 'pointer' }}>
                                <input type="checkbox" style={{ accentColor: 'var(--color-primary)', width: '16px', height: '16px' }} />
                                <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: c.color, border: c.border || 'none', boxShadow: 'inset 0 0 2px rgba(0,0,0,0.1)' }}></div>
                                <span style={{ fontSize: '14px', color: '#282c3f' }}>{c.name}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FilterSidebar;
